import * as React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  type RouteObject,
} from 'react-router-dom';
import AcceptInvitation from '../authentication/AcceptInvitation/AcceptInvitation';
import AcceptInvitationVerify from '../authentication/AcceptInvitation/AcceptInvitationVerify';
import CompleteRegistration from '../authentication/CompleteRegistration/CompleteRegistration';
import ConfirmEmail from '../authentication/ConfirmEmail/ConfirmEmail';
import { ForceSignout } from '../authentication/ForceSignout/ForceSignout';
import ForgotPassword from '../authentication/ForgotPassword/ForgotPassword';
import Register from '../authentication/Register/Register';
import ResetPassword from '../authentication/ResetPassword/ResetPassword';
import { useAuth } from '../hooks/useAuth';
import AcceptTermsAndConditions from '../authentication/AcceptTermsAndConditions/AcceptTermsAndConditions';
import { NotificationProvider } from '../components';
import { SaveBarProvider } from '../context/SaveBarProvider';
import { Dashboard } from '../views/Dashboard/Dashboard';
import { ErrorNotFound } from '../views/Error/ErrorNotFound';
import { FulfillSellerOrder } from '../views/FulfillSellerOrder';
import { Payout } from '../views/Payout';
import { Payouts } from '../views/Payouts';
import { AddProduct } from '../views/Product/AddProduct';
import { ViewProduct } from '../views/Product/ViewProduct';
import { Products } from '../views/Products';
import { SellerOrder } from '../views/SellerOrder';
import { SellerOrders } from '../views/SellerOrders';
import { Shipping } from '../views/Shipping';
import { OrderTriage } from '../views/OrderTriage';
import { LoadingBarProvider } from '../context/LoadingBarProvider';
import { ProfileSettings } from '../views/Settings/ProfileSettings';
import { StorefrontSettings } from '../views/Settings/StorefrontSettings';
import { SellingFees } from '../views/Settings/SellingFees';
import { TermsPage } from '../views/Settings/TermsPage';
import SignIn from '../authentication/SignIn/SignIn';
import { BlankLoadingPage } from '../views/BlankLoadingPage/BlankLoadingPage';
import { PaypalAccountRedirect } from '../paypal';
import { StripeAccountRedirect } from '../stripe';
import { ThemePreview } from '../views/ThemePreview';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
export const AppRouter: React.FC = () => {
  const auth = useAuth();
  const termsUpdated = !!auth && auth.user?.seller?.terms?.status === 'UPDATED';

  // Routers MUST be referentially stable across renders. They were previously
  // created inline, so every AuthProvider re-render (its context value is a
  // new object each render) rebuilt them; RouterProvider then re-subscribed to
  // the new instance and any deferred navigate() captured earlier fired on an
  // orphaned router — the URL changed but the UI didn't (broken signup
  // verify-code and post-verify redirects). Memoized on the single
  // render-time input the route elements use.
  const routers = React.useMemo(() => {
    // Every route in every router carries the same boundary. A route element
    // that throws during render otherwise lands in react-router's
    // DefaultErrorComponent, which prints error.stack into a <pre> on the
    // page — in a production build as well as a development one, since only
    // the "Hey developer" note below it is stripped by the NODE_ENV check.
    // MPPRO-154 was exactly that page, and there was no errorElement anywhere
    // in partner-web to catch it.
    //
    // Applied per route rather than by wrapping each list in a pathless parent
    // route: a parent changes how the index route and the catch-alls below
    // resolve, and nothing here needs that risk to get a boundary.
    const withErrorBoundary = (routes: RouteObject[]): RouteObject[] =>
      routes.map((route) => ({ ...route, errorElement: <RouteErrorBoundary /> }));

    const loginRouter = createBrowserRouter(withErrorBoundary([
      { path: "/login", element: <SignIn /> },
      { path: "/signup", element: <Register /> },
      { path: "/verify-code", element: <ConfirmEmail /> },
      { path: "/reset-password", element: <ResetPassword /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
      { path: "/accept-invitation/:code", element: <AcceptInvitationVerify /> },
      { path: "/accept-invitation", element: <AcceptInvitation /> },
      // Deliberately NO /complete-registration here. It is an authenticated
      // screen: it reads the signed-in user's settings and submits a mutation
      // that needs a session. Listed here, an unauthenticated visitor — a
      // bookmark, an emailed link, a browser that has been signed out —
      // rendered it instead of falling through to the redirect below, and
      // useSettings() threw during render with no user to read (MPPRO-154).
      // The catch-all sends them to sign in, which is the only thing they can
      // usefully do; the authenticated routers below keep the route.
      { path: "*", element: <Navigate to="/login" /> }
    ]));

    const registrationRouter = createBrowserRouter(withErrorBoundary([
      { path: "/logout", element: <ForceSignout /> },
      // Deliberately NO /accept-invitation routes here: acceptance flips the
      // user to signed-in-but-unregistered, which swaps in THIS router — the
      // catch-all below must then move them off the acceptance URL onto
      // /complete-registration. When these routes existed, the accepted-
      // invitation success card stayed mounted forever (its old deferred
      // navigate('/') pointed at a route this router doesn't have).
      { path: "/complete-registration", element: <CompleteRegistration /> },
      { path: "*", element: <Navigate to="/complete-registration" /> }
    ]));

    const acceptTermsRouter = createBrowserRouter(withErrorBoundary([
      { path: "/logout", element: <ForceSignout /> },
      { path: "/complete-registration", element: <CompleteRegistration /> },
      { path: "/accept-terms-and-conditions", element: <AcceptTermsAndConditions updated={termsUpdated} /> },
      { path: "*", element: <Navigate to="/accept-terms-and-conditions" /> }
    ]));

    const defaultRouter = createBrowserRouter(withErrorBoundary([
      { index: true, element: <Dashboard /> },
      { path: "/logout", element: <ForceSignout /> },
      { path: "/accept-terms-and-conditions", element: <AcceptTermsAndConditions updated={termsUpdated} /> },
      { path: "/complete-registration", element: <CompleteRegistration /> },
      { path: "/accept-invitation/:code", element: <AcceptInvitationVerify /> },
      { path: "/accept-invitation", element: <AcceptInvitation /> },
      { path: "/products/:productId", element: <ViewProduct /> },
      { path: "/products/add", element: <AddProduct /> },
      { path: "/products", element: <Products /> },
      { path: "/delivery", element: <Shipping /> },
      { path: "/orders", element: <SellerOrders /> },
      { path: "/orders/triage", element: <OrderTriage /> },
      { path: "/orders/:sellerOrderId", element: <SellerOrder /> },
      { path: "/orders/:sellerOrderId/fulfill", element: <FulfillSellerOrder /> },
      { path: "/settings/storefront/*", element: <StorefrontSettings /> },
      { path: "/settings/selling-fees", element: <SellingFees /> },
      { path: "/settings/terms", element: <TermsPage /> },
      { path: "/settings/*", element: <ProfileSettings /> },
      { path: "/payouts", element: <Payouts /> },
      { path: "/payouts/:payoutId", element: <Payout /> },
      { path: "/stripe-redirect/:type", element: <StripeAccountRedirect /> },
      { path: "/paypal-redirect", element: <PaypalAccountRedirect /> },
      { path: "*", element: <ErrorNotFound /> },
      { path: "/login", element: <Navigate to={"/"} /> },
    ]));

    const loggingOutRouter = createBrowserRouter(withErrorBoundary([
      { path: "*", element: <BlankLoadingPage message='Logging out...' /> }
    ]));

    const loggingInRouter = createBrowserRouter(withErrorBoundary([
      { path: "*", element: <BlankLoadingPage message='Logging in...' /> }
    ]));

    const errorRouter = createBrowserRouter(withErrorBoundary([
      {
        path: "*",
        element: <ErrorNotFound />
      }
    ]));

    return { loginRouter, registrationRouter, acceptTermsRouter, defaultRouter, loggingOutRouter, loggingInRouter, errorRouter };
  }, [termsUpdated]);

  // The admin theming editor iframes this static showcase as its live preview.
  // It must render identically for any auth state (the iframe is never logged
  // in) and without the logging-in/out interstitials, so it bypasses the
  // auth-dependent router selection entirely. (After the hooks above — hook
  // order must not depend on the pathname.)
  if (window.location.pathname === '/theme-preview') {
    return <ThemePreview />;
  }

  const { loginRouter, registrationRouter, acceptTermsRouter, defaultRouter, loggingOutRouter, loggingInRouter, errorRouter } = routers;

  const currentRouter =
    auth && (
      auth.loggingIn ?
        loggingInRouter :
        auth.isLoggedIn ?
          auth.loggingOut ?
            loggingOutRouter :
            auth.isRegistered ?
              auth.user?.seller?.terms?.status === "ACCEPTED" ?
                defaultRouter : acceptTermsRouter : registrationRouter : loginRouter) || errorRouter;

  return (
    <NotificationProvider>
      <LoadingBarProvider>
        <SaveBarProvider>
          <RouterProvider router={currentRouter} />
        </SaveBarProvider>
      </LoadingBarProvider>
    </NotificationProvider>
  );
};
